import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/lib/api'
import type { CommonCode, Region } from '@/shared/lib/apiTypes'

export function useCommonCodes(group?: string) {
  return useQuery({
    queryKey: ['common-codes', group ?? 'all'],
    queryFn: () => api.commonCodes(group),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.items,
  })
}

export const TARGET_LIFECYCLE = 'target_lifecycle'
export const TARGET_SITUATION = 'target_situation'

export function useTargetCodes(): { data: CommonCode[] | undefined; isPending: boolean } {
  const lifecycle = useCommonCodes(TARGET_LIFECYCLE)
  const situation = useCommonCodes(TARGET_SITUATION)
  return {
    data:
      lifecycle.data && situation.data ? [...lifecycle.data, ...situation.data] : undefined,
    isPending: lifecycle.isPending || situation.isPending,
  }
}

export function useRegions() {
  return useQuery({
    queryKey: ['regions'],
    queryFn: () => api.regions(),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.items,
  })
}

export function useDongs() {
  const q = useRegions()
  return { ...q, data: q.data?.filter((r) => r.level === 'emd') }
}

export type CodeLabeler = (code: string) => string

export function makeLabeler(codes: CommonCode[] | undefined): CodeLabeler {
  const map = new Map((codes ?? []).map((c) => [c.code, c.label]))
  return (code) => map.get(code) ?? code
}

export function makeRegionLabeler(regions: Region[] | undefined): CodeLabeler {
  const map = new Map((regions ?? []).map((r) => [r.code, r.name]))
  return (code) => map.get(code) ?? code
}

export const URGENCY_LABEL: Record<string, string> = {
  high: '시급',
  mid: '보통',
  low: '여유',
}

export const CERT_LABEL: Record<string, string> = {
  cert_certified: '인증',
  cert_preliminary: '예비',
}
